import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SEO from '../../../../shared/components/SEO/SEO';
import { useAuth } from '../../../../contexts/AuthContext';
import { forumAPI, type ForumCategory, type ForumPost } from '../../../../services/api';
import { stripHtml } from './utils';
import { UserAvatar } from './UserAvatar';
import './Community.css';

const CategoryPosts: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const pageSize = 20;
  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    forumAPI.getCategories()
      .then((res) => setCategories(res.data as ForumCategory[]))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    const params: { category: string; search?: string; page: number; page_size: number } = {
      category: slug,
      page,
      page_size: pageSize,
    };
    if (search) params.search = search;
    forumAPI.getPosts(params)
      .then((res: { data: { results: ForumPost[]; count: number } }) => {
        setPosts(res.data.results);
        setCount(res.data.count);
      })
      .catch(() => {
        setPosts([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
  }, [slug, search, page]);

  const totalPages = Math.ceil(count / pageSize) || 1;

  const handleLike = useCallback(
    (e: React.MouseEvent, postId: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isAuthenticated) {
        navigate('/login?next=' + encodeURIComponent('/community/c/' + slug));
        return;
      }
      forumAPI
        .likePost(postId)
        .then((res) => {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === postId ? { ...p, liked: res.data.liked, like_count: res.data.like_count } : p
            )
          );
        })
        .catch(() => {});
    },
    [isAuthenticated, navigate, slug]
  );

  return (
    <>
      <SEO
        title={category ? `${category.name} - Crypxa Community` : 'Community - Crypxa'}
        description={category?.description || 'Browse forum posts.'}
      />
      <div className="community-page">
        <div className="community-header">
          <h1>{category?.name ?? slug ?? 'Category'}</h1>
          {isAuthenticated ? (
            <Link to="/community/new" className="community-btn">+ New post</Link>
          ) : (
            <Link to={'/login?next=' + encodeURIComponent('/community/new')} className="community-btn">Log in to post</Link>
          )}
        </div>
        {category?.description && (
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.95rem' }}>
            {category.description}
          </p>
        )}
        <div className="community-categories">
          {isAuthenticated && (
            <Link to="/community/my-posts" className="community-cat-link community-my-posts">My posts</Link>
          )}
          <Link to="/community" className="community-cat-link">All</Link>
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/community/c/${cat.slug}`}
              className={`community-cat-link ${slug === cat.slug ? 'active' : ''}`}
            >
              {cat.name} ({cat.post_count})
            </Link>
          ))}
        </div>
        <div className="community-search">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearch((e.currentTarget.elements.namedItem('q') as HTMLInputElement)?.value || '');
            }}
          >
            <input type="search" name="q" placeholder="Search in this category..." defaultValue={search} />
          </form>
        </div>
        {loading ? (
          <div className="community-empty">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="community-empty">No posts in this category yet.</div>
        ) : (
          <>
            <div className="community-post-list">
              {posts.map((post) => (
                <Link key={post.id} to={`/community/p/${post.id}`} className="community-post-card">
                  <div className="community-card-media">
                    {post.attachments && post.attachments.length > 0 ? (
                      <img src={post.attachments[0].file_url} alt="" />
                    ) : (
                      <div className="community-card-placeholder">
                        <span className="community-card-placeholder-icon" aria-hidden>🖼️</span>
                        <span>No image</span>
                      </div>
                    )}
                  </div>
                  <div className="community-card-body">
                    <div className="meta">
                      {post.is_pinned && <span className="pinned">Pinned</span>}
                      <UserAvatar user={post.user} />
                      <span>{post.user.display_name}</span>
                      <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p className="excerpt">{stripHtml(post.body).slice(0, 180)}{stripHtml(post.body).length > 180 ? '...' : ''}</p>
                    {post.attachments && post.attachments.length > 1 && (
                      <div className="community-card-images">
                        {post.attachments.slice(1, 5).map((att) => (
                          <span key={att.id} className="community-card-thumb">
                            <img src={att.file_url} alt="" />
                          </span>
                        ))}
                        {post.attachments.length > 5 && (
                          <span className="community-card-thumb community-card-thumb-more">+{post.attachments.length - 5}</span>
                        )}
                      </div>
                    )}
                    <div className="stats">
                      <button type="button" className={`community-stat-item stat-likes ${post.liked ? 'liked' : ''}`} onClick={(e) => handleLike(e, post.id)} aria-label={post.liked ? 'Unlike' : 'Like'}>
                        {(post.like_count ?? 0) > 0 || post.liked ? <i className={`fas fa-heart${post.liked ? '' : '-o'}`} aria-hidden></i> : null} {post.like_count ?? 0}
                      </button>
                      <span className="community-stat-item stat-comments"><i className="fas fa-comment" aria-hidden></i> {post.comment_count ?? 0}</span>
                      <button type="button" className="community-stat-item stat-share" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard?.writeText(`${window.location.origin}/community/p/${post.id}`); }} aria-label="Copy link"><i className="fas fa-share-alt" aria-hidden></i> Share</button>
                      <span className="community-stat-item stat-views"><i className="fas fa-eye" aria-hidden></i> {post.views}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="community-pagination">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                <span style={{ alignSelf: 'center', fontSize: '0.9rem' }}>Page {page} of {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CategoryPosts;
